/**
 * Dashboard Card Registry
 *
 * Defines all dashboard cards with metadata used for rendering,
 * preferences, and the configurator panel.
 *
 * Card sizes:
 *   'stat'  – small stat card (fits 4 per row on desktop)
 *   'half'  – half-width card (fits 2 per row on desktop)
 *   'full'  – full-width card (1 per row)
 */

const DASHBOARD_CARDS = [
  // === Key Metric Stat Cards ===
  {
    id: 'total_accounts',
    label: 'Total Accounts',
    category: 'metrics',
    defaultVisible: true,
    defaultOrder: 0,
    defaultSize: 'stat',
    module: 'accounts',
    permission: 'view',
    locked: false,
  },
  {
    id: 'active_contacts',
    label: 'Active Contacts',
    category: 'metrics',
    defaultVisible: true,
    defaultOrder: 1,
    defaultSize: 'stat',
    module: 'contacts',
    permission: 'view',
    locked: false,
  },
  {
    id: 'open_tasks',
    label: 'Open Action Items',
    category: 'metrics',
    defaultVisible: true,
    defaultOrder: 2,
    defaultSize: 'stat',
    module: 'tasks',
    permission: 'view',
    locked: false,
  },
  {
    id: 'overdue_tasks',
    label: 'Overdue Action Items',
    category: 'metrics',
    defaultVisible: true,
    defaultOrder: 3,
    defaultSize: 'stat',
    module: 'tasks',
    permission: 'view',
    locked: false,
  },

  // === Opportunity Stat Cards ===
  {
    id: 'open_opportunities',
    label: 'Open Opportunities',
    category: 'metrics',
    defaultVisible: true,
    defaultOrder: 4,
    defaultSize: 'stat',
    module: 'opportunities',
    permission: 'view',
    locked: false,
  },
  {
    id: 'pipeline_value',
    label: 'Pipeline Value',
    category: 'metrics',
    defaultVisible: true,
    defaultOrder: 5,
    defaultSize: 'stat',
    module: 'opportunities',
    permission: 'view',
    locked: false,
  },
  {
    id: 'won_opportunities',
    label: 'Won Opportunities',
    category: 'metrics',
    defaultVisible: true,
    defaultOrder: 6,
    defaultSize: 'stat',
    module: 'opportunities',
    permission: 'view',
    locked: false,
  },
  {
    id: 'won_value',
    label: 'Won Value',
    category: 'metrics',
    defaultVisible: true,
    defaultOrder: 7,
    defaultSize: 'stat',
    module: 'opportunities',
    permission: 'view',
    locked: false,
  },

  // === Pipeline Cards ===
  {
    id: 'account_pipeline',
    label: 'Account Pipeline',
    category: 'pipeline',
    defaultVisible: true,
    defaultOrder: 8,
    defaultSize: 'half',
    module: 'accounts',
    permission: 'view',
    locked: false,
  },
  {
    id: 'opportunity_pipeline',
    label: 'Opportunity Pipeline',
    category: 'pipeline',
    defaultVisible: true,
    defaultOrder: 9,
    defaultSize: 'half',
    module: 'opportunities',
    permission: 'view',
    locked: false,
  },

  // === Action Item & Summary Cards ===
  {
    id: 'upcoming_tasks',
    label: 'Upcoming Action Items',
    category: 'lists',
    defaultVisible: true,
    defaultOrder: 10,
    defaultSize: 'half',
    module: 'tasks',
    permission: 'view',
    locked: false,
  },
  {
    id: 'quotes_summary',
    label: 'Quotes Summary',
    category: 'summary',
    defaultVisible: true,
    defaultOrder: 11,
    defaultSize: 'half',
    module: 'quotes',
    permission: 'view',
    locked: false,
  },
  {
    id: 'orders_summary',
    label: 'Orders Summary',
    category: 'summary',
    defaultVisible: true,
    defaultOrder: 12,
    defaultSize: 'half',
    module: 'orders',
    permission: 'view',
    locked: false,
  },

  // === Activity Card ===
  {
    id: 'recent_activity',
    label: 'Recent Activity',
    category: 'activity',
    defaultVisible: true,
    defaultOrder: 13,
    defaultSize: 'full',
    module: 'activities',
    permission: 'view',
    locked: false,
  },

  // === Report Widget Cards ===
  {
    id: 'pipeline_chart',
    label: 'Pipeline Overview',
    category: 'reports',
    defaultVisible: false,
    defaultOrder: 14,
    defaultSize: 'half',
    module: 'reports',
    permission: 'view',
    locked: false,
  },
  {
    id: 'revenue_trend',
    label: 'Revenue Trend',
    category: 'reports',
    defaultVisible: false,
    defaultOrder: 15,
    defaultSize: 'half',
    module: 'reports',
    permission: 'view',
    locked: false,
  },
  {
    id: 'forecast_summary',
    label: 'Forecast Summary',
    category: 'reports',
    defaultVisible: false,
    defaultOrder: 16,
    defaultSize: 'half',
    module: 'reports',
    permission: 'view',
    locked: false,
  },
];

const CARD_SIZES = ['stat', 'half', 'full'];

const CARD_CATEGORIES = {
  metrics: 'Key Metrics',
  pipeline: 'Pipelines',
  lists: 'Lists',
  summary: 'Summaries',
  activity: 'Activity',
  reports: 'Reports',
};

/**
 * Returns default dashboard preferences (all cards visible in default order).
 */
function getDefaultDashboardPreferences() {
  return DASHBOARD_CARDS.map(card => ({
    id: card.id,
    visible: card.defaultVisible,
    order: card.defaultOrder,
    size: card.defaultSize,
  }));
}

/**
 * Get a card's registry entry by ID.
 */
function getCardById(cardId) {
  return DASHBOARD_CARDS.find(c => c.id === cardId) || null;
}

export {
  DASHBOARD_CARDS,
  CARD_SIZES,
  CARD_CATEGORIES,
  getDefaultDashboardPreferences,
  getCardById,
};
